/**
 * TanStack Query hooks for Closing Disclosure API
 *
 * Provides server state management with caching, background refetching,
 * and optimistic updates for CD transactions.
 */

import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { closingDisclosureApi } from '../services/closingDisclosureApi';
import { useClosingDisclosureStore } from '../store/closingDisclosureStore';
import type {
  CalculatedTotals,
  LineItem,
  LoanTerms,
  Party,
  Transaction,
  ValidationResult,
} from '../types';

/**
 * Query keys for cache management
 */
export const cdQueryKeys = {
  all: ['closing-disclosure'] as const,
  transactions: () => [...cdQueryKeys.all, 'transactions'] as const,
  transaction: (id: string) => [...cdQueryKeys.transactions(), id] as const,
  transactionDetails: (id: string) => [...cdQueryKeys.transaction(id), 'details'] as const,
  loanTerms: (transactionId: string) =>
    [...cdQueryKeys.transaction(transactionId), 'loan-terms'] as const,
  parties: (transactionId: string) =>
    [...cdQueryKeys.transaction(transactionId), 'parties'] as const,
  lineItems: (transactionId: string) =>
    [...cdQueryKeys.transaction(transactionId), 'line-items'] as const,
  calculations: (transactionId: string) =>
    [...cdQueryKeys.transaction(transactionId), 'calculations'] as const,
  validation: (transactionId: string) =>
    [...cdQueryKeys.transaction(transactionId), 'validation'] as const,
};

// ============================================================================
// Transaction Queries
// ============================================================================

/**
 * Fetch a single transaction by ID
 */
export function useTransaction(
  id: string,
  options?: Omit<UseQueryOptions<Transaction, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: cdQueryKeys.transaction(id),
    queryFn: async () => {
      const response = await closingDisclosureApi.getById(id);
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
}

/**
 * Fetch list of transactions with optional filters
 */
export function useTransactions(
  params?: { status?: string; limit?: number; offset?: number },
  options?: Omit<
    UseQueryOptions<{ transactions: Transaction[]; total: number }, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: [...cdQueryKeys.transactions(), params],
    queryFn: async () => {
      const response = await closingDisclosureApi.list(params);
      return response.data;
    },
    ...options,
  });
}

/**
 * Create a new transaction
 */
export function useCreateTransaction(
  options?: Omit<
    UseMutationOptions<
      { transactionId: string },
      Error,
      Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'>
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transaction) => {
      const response = await closingDisclosureApi.create(transaction);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transactions() });
    },
    ...options,
  });
}

/**
 * Update an existing transaction
 */
export function useUpdateTransaction(
  options?: Omit<
    UseMutationOptions<Transaction, Error, { id: string; data: Partial<Transaction> }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();
  const { setSyncStatus, markSaved } = useClosingDisclosureStore();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      setSyncStatus('saving');
      const response = await closingDisclosureApi.update(id, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transaction(variables.id) });
      markSaved();
    },
    onError: () => {
      setSyncStatus('error');
    },
    ...options,
  });
}

/**
 * Delete a transaction
 */
export function useDeleteTransaction(
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await closingDisclosureApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transactions() });
    },
    ...options,
  });
}

// ============================================================================
// Loan Terms Queries
// ============================================================================

/**
 * Fetch loan terms for a transaction
 */
export function useLoanTerms(
  transactionId: string,
  options?: Omit<UseQueryOptions<LoanTerms, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: cdQueryKeys.loanTerms(transactionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getLoanTerms(transactionId);
      return response.data;
    },
    enabled: !!transactionId,
    ...options,
  });
}

/**
 * Save loan terms for a transaction
 */
export function useSaveLoanTerms(
  options?: Omit<
    UseMutationOptions<
      LoanTerms,
      Error,
      { transactionId: string; loanTerms: Omit<LoanTerms, 'id' | 'transactionId'> }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, loanTerms }) => {
      const response = await closingDisclosureApi.saveLoanTerms(transactionId, loanTerms);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.loanTerms(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.calculations(variables.transactionId),
      });
    },
    ...options,
  });
}

// ============================================================================
// Parties Queries
// ============================================================================

/**
 * Fetch parties for a transaction
 */
export function useParties(
  transactionId: string,
  options?: Omit<UseQueryOptions<Party[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: cdQueryKeys.parties(transactionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getParties(transactionId);
      return response.data;
    },
    enabled: !!transactionId,
    ...options,
  });
}

/**
 * Save parties for a transaction
 */
export function useSaveParties(
  options?: Omit<
    UseMutationOptions<Party[], Error, { transactionId: string; parties: Omit<Party, 'id'>[] }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, parties }) => {
      const response = await closingDisclosureApi.saveParties(transactionId, parties);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.parties(variables.transactionId),
      });
    },
    ...options,
  });
}

/**
 * Update a single party
 */
export function useUpdateParty(
  options?: Omit<
    UseMutationOptions<
      Party,
      Error,
      { transactionId: string; partyId: string; data: Partial<Party> }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, partyId, data }) => {
      const response = await closingDisclosureApi.updateParty(transactionId, partyId, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.parties(variables.transactionId),
      });
    },
    ...options,
  });
}

// ============================================================================
// Line Items Queries
// ============================================================================

/**
 * Fetch line items for a transaction
 */
export function useLineItems(
  transactionId: string,
  options?: Omit<UseQueryOptions<LineItem[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: cdQueryKeys.lineItems(transactionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getLineItems(transactionId);
      return response.data;
    },
    enabled: !!transactionId,
    ...options,
  });
}

/**
 * Save line items for a transaction
 */
export function useSaveLineItems(
  options?: Omit<
    UseMutationOptions<
      LineItem[],
      Error,
      { transactionId: string; items: (LineItem & { id?: string })[] }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, items }) => {
      const response = await closingDisclosureApi.saveLineItems(transactionId, items);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.lineItems(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.calculations(variables.transactionId),
      });
    },
    ...options,
  });
}

/**
 * Update a single line item
 */
export function useUpdateLineItem(
  options?: Omit<
    UseMutationOptions<
      LineItem,
      Error,
      { transactionId: string; itemId: string; data: Partial<LineItem> }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, itemId, data }) => {
      const response = await closingDisclosureApi.updateLineItem(transactionId, itemId, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.lineItems(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.calculations(variables.transactionId),
      });
    },
    ...options,
  });
}

/**
 * Delete a line item
 */
export function useDeleteLineItem(
  options?: Omit<
    UseMutationOptions<void, Error, { transactionId: string; itemId: string }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, itemId }) => {
      await closingDisclosureApi.deleteLineItem(transactionId, itemId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.lineItems(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.calculations(variables.transactionId),
      });
    },
    ...options,
  });
}

// ============================================================================
// Calculations & Validation Queries
// ============================================================================

/**
 * Fetch calculated totals for a transaction
 */
export function useCalculations(
  transactionId: string,
  options?: Omit<UseQueryOptions<CalculatedTotals, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: cdQueryKeys.calculations(transactionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getCalculations(transactionId);
      return response.data;
    },
    enabled: !!transactionId,
    ...options,
  });
}

/**
 * Validate a transaction
 */
export function useValidateTransaction(
  transactionId: string,
  options?: Omit<UseQueryOptions<ValidationResult, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: cdQueryKeys.validation(transactionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.validateTransaction(transactionId);
      return response.data;
    },
    enabled: !!transactionId,
    ...options,
  });
}

// ============================================================================
// PDF Generation
// ============================================================================

/**
 * Generate PDF for a transaction
 */
export function useGeneratePDF(
  options?: Omit<
    UseMutationOptions<
      { filename: string },
      Error,
      { transactionId: string; version?: 'draft' | 'final'; includeWatermark?: boolean }
    >,
    'mutationFn'
  >,
) {
  return useMutation({
    mutationFn: async ({ transactionId, version = 'draft', includeWatermark = true }) => {
      const response = await closingDisclosureApi.generatePDF(
        transactionId,
        version,
        includeWatermark,
      );
      return { filename: response.filename };
    },
    ...options,
  });
}

/**
 * Download PDF for a transaction
 */
export function useDownloadPDF(
  options?: Omit<
    UseMutationOptions<Blob, Error, { transactionId: string; documentId: string }>,
    'mutationFn'
  >,
) {
  return useMutation({
    mutationFn: async ({ transactionId, documentId }) => {
      return closingDisclosureApi.downloadPDF(transactionId, documentId);
    },
    ...options,
  });
}
